import React, { useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Box, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip,
  TextField, Button, Stack, Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const ALL = '__ALL__';
const NONE = '__NONE__';

const MULTI_PROPS = {
  multiple: true,
  input: <OutlinedInput label="" />,
  renderValue: (selected) =>
    selected.length === 0
      ? 'All'
      : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => (<Chip key={value} label={value} size="small" />))}
        </Box>
      ),
};

const norm = (v) => (v ?? '').toString().trim();
const ci = (s) => norm(s).toLowerCase();
const isDivisionSite = (s) => ci(s) === 'division';
const sortAlpha = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });

// Build row label "software — version" (version optional)
const appKey = (row) => {
  const s = norm(row.software);
  const v = norm(row.version);
  return v ? `${s} — ${v}` : s;
};

// UI-only label helper
const labelFor = (field) => (field === 'department' ? 'Support Department'
  : field.charAt(0).toUpperCase() + field.slice(1));

const LicensesMatrix = ({ licenses = [] }) => {
  const theme = useTheme();

  // ONE source of truth for colors (legend + cells)
  const COLORS = useMemo(() => ({
    both:   { bg: theme.palette.success.main,  border: theme.palette.success.dark  },
    one:    { bg: theme.palette.warning.main,  border: theme.palette.warning.dark  },
    none:   { bg: theme.palette.error.main,    border: theme.palette.error.dark    },
    webapp: { bg: theme.palette.info.main,     border: theme.palette.info.dark     },
  }), [theme]);

  // ---------- FILTER STATE ----------
  const [sitesFilter, setSitesFilter] = useState([]);
  const [catsFilter, setCatsFilter] = useState([]);
  const [deptsFilter, setDeptsFilter] = useState([]);
  const [vendorsFilter, setVendorsFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [descsFilter, setDescsFilter] = useState([]);
  const [query, setQuery] = useState('');

  const clearFilters = () => {
    setSitesFilter([]);
    setCatsFilter([]);
    setDeptsFilter([]);
    setVendorsFilter([]);
    setStatusFilter([]);
    setDescsFilter([]);
    setQuery('');
  };

  // ---------- OPTIONS ----------
  const allSites = useMemo(
    () =>
      [...new Set(licenses.map(l => norm(l.site)).filter(Boolean))]
        .filter(s => !isDivisionSite(s))
        .sort(sortAlpha),
    [licenses]
  );

  const allCategories = useMemo(
    () => [...new Set(licenses.map(l => norm(l.category)).filter(Boolean))].sort(sortAlpha),
    [licenses]
  );

  const allDepartments = useMemo(
    () => [...new Set(licenses.map(l => norm(l.department)).filter(Boolean))].sort(sortAlpha),
    [licenses]
  );

  const allVendors = useMemo(
    () => [...new Set(licenses.map(l => norm(l.vendor)).filter(Boolean))].sort(sortAlpha),
    [licenses]
  );

  const allStatus = useMemo(
    () => [...new Set(licenses.map(l => norm(l.status)).filter(Boolean))].sort(sortAlpha),
    [licenses]
  );

  const allDescriptions = useMemo(
    () => [...new Set(licenses.map(l => norm(l.description)).filter(Boolean))].sort(sortAlpha),
    [licenses]
  );

  // ---------- SELECT HANDLERS (support All / None) ----------
  const handleMultiChange = (setter) => (e) => {
    const value = e.target.value;
    const arr = typeof value === 'string' ? value.split(',') : value;

    if (arr.includes(ALL)) { setter([]); return; }   // All = clear filter
    if (arr.includes(NONE)) { setter([NONE]); return; } // None = only empty values
    setter(arr);
  };

  // ---------- APPLY FILTERS ----------
  const filteredRows = useMemo(() => {
    const q = ci(query);

    const matchGeneric = (value, selected) => {
      const v = norm(value);
      if (selected.length === 0) return true;
      if (selected.includes(NONE)) return v === '';
      return selected.includes(v);
    };

    const matchSite = (site, selected) => {
      const s = norm(site);
      if (selected.length === 0) return true;
      if (selected.includes(NONE)) return s === '';
      return isDivisionSite(s) || selected.includes(s);
    };

    return licenses.filter((l) => {
      const site = l.site;
      const category = l.category;
      const dept = l.department;
      const vendor = l.vendor;
      const status = l.status;
      const software = l.software;
      const description = l.description;
      const version = l.version;

      const siteMatches   = matchSite(site, sitesFilter);
      const catMatches    = matchGeneric(category, catsFilter);
      const deptMatches   = matchGeneric(dept, deptsFilter);
      const vendorMatches = matchGeneric(vendor, vendorsFilter);
      const statusMatches = matchGeneric(status, statusFilter);
      const descMatches   = matchGeneric(description, descsFilter);

      const queryMatches =
        q.length === 0 ||
        ci(software).includes(q) ||
        ci(description).includes(q) ||
        ci(version).includes(q);

      return (
        siteMatches &&
        catMatches &&
        deptMatches &&
        vendorMatches &&
        statusMatches &&
        descMatches &&
        queryMatches
      );
    });
  }, [licenses, sitesFilter, catsFilter, deptsFilter, vendorsFilter, statusFilter, descsFilter, query]);

  // software — version keys
  const appKeys = useMemo(
    () => [...new Set(filteredRows.map(l => appKey(l)).filter(Boolean))].sort(sortAlpha),
    [filteredRows]
  );

  const sites = useMemo(() => {
    if (sitesFilter.length === 0) return allSites;
    if (sitesFilter.includes(NONE)) return [];
    return [...sitesFilter].sort(sortAlpha);
  }, [allSites, sitesFilter]);

  // Build status matrix: { appKey: { site: { present, rti, pkg, web } } }
  const statusMatrix = useMemo(() => {
    const map = {};
    appKeys.forEach(k => {
      map[k] = Object.fromEntries(
        sites.map(s => [s, { present: false, rti: false, pkg: false, web: false }])
      );
    });

    const applyRowToSites = (row, targetSites) => {
      const key = appKey(row);
      targetSites.forEach(site => {
        const cell = map[key]?.[site];
        if (!cell) return;
        cell.present = true;
        cell.rti = cell.rti || !!row.ready_to_install;
        cell.pkg = cell.pkg || !!row.packaged;
        cell.web = cell.web || !!row.webapp;
      });
    };

    filteredRows.forEach(row => {
      const site = norm(row.site);
      if (isDivisionSite(site)) {
        applyRowToSites(row, sites); // Division applies to all columns
      } else if (sites.includes(site)) {
        applyRowToSites(row, [site]);
      }
    });

    return map;
  }, [appKeys, sites, filteredRows]);

  // per-row counts: present sites / total visible sites
  const countsByApp = useMemo(() => {
    const out = {};
    appKeys.forEach(k => {
      const cells = statusMatrix[k] ? Object.values(statusMatrix[k]) : [];
      out[k] = cells.filter(c => c.present).length;
    });
    return out;
  }, [statusMatrix, appKeys]);

  const nothingToShow = !appKeys.length || !sites.length;

  // color chooser for a cell (webapp blue has priority)
  const cellColors = (cell) => {
    if (!cell?.present) return { bg: 'transparent', border: 'divider' };
    if (cell.web)      return COLORS.webapp; // webapp overrides
    const bothTrue = cell.rti && cell.pkg;
    const oneTrue  = (cell.rti && !cell.pkg) || (!cell.rti && cell.pkg);
    if (bothTrue)   return COLORS.both;
    if (oneTrue)    return COLORS.one;
    return COLORS.none;
  };

  return (
    <Paper sx={{ mt: 3, p: 2 }}>
      {/* Filters */}
      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2, rowGap: 2 }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Site</InputLabel>
          <Select label="Site" value={sitesFilter} onChange={handleMultiChange(setSitesFilter)} {...MULTI_PROPS}>
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value={NONE}>None</MenuItem>
            {allSites.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={catsFilter} onChange={handleMultiChange(setCatsFilter)} {...MULTI_PROPS}>
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value={NONE}>None</MenuItem>
            {allCategories.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>{labelFor('department')}</InputLabel>
          <Select label={labelFor('department')} value={deptsFilter} onChange={handleMultiChange(setDeptsFilter)} {...MULTI_PROPS}>
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value={NONE}>None</MenuItem>
            {allDepartments.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        {/* Description */}
        <FormControl sx={{ minWidth: 220 }} size="small">
          <InputLabel>Description</InputLabel>
          <Select label="Description" value={descsFilter} onChange={handleMultiChange(setDescsFilter)} {...MULTI_PROPS}>
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value={NONE}>None</MenuItem>
            {allDescriptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Vendor</InputLabel>
          <Select label="Vendor" value={vendorsFilter} onChange={handleMultiChange(setVendorsFilter)} {...MULTI_PROPS}>
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value={NONE}>None</MenuItem>
            {allVendors.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={handleMultiChange(setStatusFilter)} {...MULTI_PROPS}>
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value={NONE}>None</MenuItem>
            {allStatus.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          size="small"
          sx={{ minWidth: 260, flexGrow: 1 }}
          label="Search (App / Version / Description)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <Button variant="outlined" onClick={clearFilters}>Clear</Button>
      </Stack>

      {/* Legend / Key */}
      <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 18, height: 18, borderRadius: '4px', border: '1px solid', backgroundColor: COLORS.both.bg, borderColor: COLORS.both.border }} />
          <span>Ready to Install + Packaged</span>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 18, height: 18, borderRadius: '4px', border: '1px solid', backgroundColor: COLORS.one.bg, borderColor: COLORS.one.border }} />
          <span>Only Ready OR Packaged</span>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 18, height: 18, borderRadius: '4px', border: '1px solid', backgroundColor: COLORS.none.bg, borderColor: COLORS.none.border }} />
          <span>Neither</span>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 18, height: 18, borderRadius: '4px', border: '1px solid', backgroundColor: COLORS.webapp.bg, borderColor: COLORS.webapp.border }} />
          <span>Web App</span>
        </Stack>
      </Stack>

      {/* Matrix */}
      <TableContainer sx={{ maxHeight: 540 }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            '& td, & th': { py: 0.5, px: 1 },
            '& th.sticky, & td.sticky': {
              position: 'sticky', left: 0, background: '#fff', zIndex: 2,
            },
            '& th.sticky': { zIndex: 3 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell className="sticky" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                Application (Version)
              </TableCell>
              {sites.map(site => (
                <TableCell key={site} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  {site}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {nothingToShow ? (
              <TableRow>
                <TableCell colSpan={sites.length + 1} align="center">
                  No results with current filters.
                </TableCell>
              </TableRow>
            ) : (
              appKeys.map(key => (
                <TableRow key={key}>
                  <TableCell className="sticky" sx={{ fontWeight: 500 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{key}</span>
                      <Chip
                        size="small"
                        label={`${countsByApp[key] || 0}/${sites.length}`}
                        sx={{ bgcolor: 'action.selected' }}
                      />
                    </Stack>
                  </TableCell>

                  {sites.map(site => {
                    const cell = statusMatrix[key][site]; // {present, rti, pkg, web}
                    const { bg, border } = cellColors(cell);
                    const tip = cell?.present
                      ? `${cell.web ? 'Web App • ' : ''}Ready to Install: ${cell.rti ? 'Yes' : 'No'} • Packaged: ${cell.pkg ? 'Yes' : 'No'}`
                      : 'No license for this site';

                    return (
                      <TableCell key={site} align="center">
                        <Tooltip title={tip} arrow>
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              mx: 'auto',
                              borderRadius: '4px',
                              border: '1px solid',
                              borderColor: border,
                              backgroundColor: bg,
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default LicensesMatrix;